import { useCallback, useEffect, useState } from "react";
import "@/index.css";
import Ventures from "@/Ventures";
import Capture from "@/Capture";
import AlignmentEditor from "@/AlignmentEditor";
import {
  api,
  TYPE_LABELS,
  todayISO,
  formatLongDate,
} from "@/lib/marsheeApi";

function Header({ dateISO, view, onNav, onLogout }) {
  const link = (key, label) => (
    <button
      data-testid={`nav-${key}`}
      onClick={() => onNav(key)}
      aria-current={view === key ? "page" : undefined}
      className="mh-sans"
      style={{
        fontSize: "0.8125rem", letterSpacing: "0.06em", background: "none", cursor: "pointer",
        color: view === key ? "#ECE5DB" : "#8C7B6C",
        borderBottom: view === key ? "1px solid #AD8F60" : "1px solid transparent",
        paddingBottom: "3px",
      }}
    >
      {label}
    </button>
  );
  return (
    <header data-testid="app-header" style={{ backgroundColor: "#241D18", color: "#ECE5DB" }}>
      <div className="mx-auto max-w-4xl px-6 sm:px-12 py-5 flex items-baseline justify-between gap-6">
        <div>
          <div className="mh-eyebrow" data-testid="brand-mark" style={{ color: "#C6B39F" }}>Marshee HQ</div>
          <div className="mh-label mt-1" data-testid="header-date" style={{ color: "#8C7B6C" }}>{formatLongDate(dateISO)}</div>
        </div>
        <nav aria-label="Primary" className="flex items-center gap-6">
          {link("today", "Today")}
          {link("ventures", "Ventures")}
          {link("capture", "Capture")}
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            className="mh-sans"
            style={{
              fontSize: "0.8125rem", letterSpacing: "0.06em", background: "none", cursor: "pointer",
              color: "#6E5E50", borderBottom: "1px solid transparent", paddingBottom: "3px",
            }}
            title="Sign out"
          >
            Sign out
          </button>
        </nav>
      </div>
    </header>
  );
}

function SectionLabel({ children, testid }) {
  return (
    <div className="mh-label mb-5" data-testid={testid}>
      {children}
    </div>
  );
}

function NorthStar({ alignment }) {
  const season = alignment?.season;
  const strategy = alignment?.strategy;
  const outcome = alignment?.primary_outcome;

  if (!season) {
    return (
      <section className="mh-fade" data-testid="northstar-empty">
        <SectionLabel testid="northstar-label">Current Chapter</SectionLabel>
        <p className="mh-sans" style={{ fontSize: "1rem", lineHeight: 1.6, color: "var(--mh-muted)", maxWidth: "40ch" }}>
          No chapter has been set yet — when you name the season you are living, it will anchor everything here.
        </p>
      </section>
    );
  }

  return (
    <section className="mh-fade" data-testid="northstar">
      <SectionLabel testid="northstar-label">Current Chapter</SectionLabel>
      <h2
        className="mh-serif"
        data-testid="northstar-season"
        style={{ fontSize: "clamp(2rem, 5vw, 3.25rem)", lineHeight: 1.08, letterSpacing: "-0.01em", color: "var(--mh-text)", maxWidth: "18ch" }}
      >
        {season.title}
      </h2>

      <div className="mt-8 space-y-5" style={{ maxWidth: "54ch" }}>
        {season.description && (
          <p className="mh-serif" data-testid="northstar-season-desc" style={{ fontSize: "1.2rem", lineHeight: 1.6, color: "var(--mh-action)" }}>
            {season.description}
          </p>
        )}
        {strategy && (
          <p className="mh-serif" data-testid="northstar-strategy" style={{ fontSize: "1.2rem", lineHeight: 1.6, color: "var(--mh-action)" }}>
            You are approaching it through <span style={{ color: "var(--mh-text)", fontStyle: "italic" }}>{strategy.title}</span>
            {strategy.description ? ` — ${strategy.description}` : "."}
          </p>
        )}
        {outcome ? (
          <div className="pt-2" data-testid="northstar-outcome">
            <span className="mh-label">What matters most right now</span>
            <p className="mh-serif mt-2" style={{ fontSize: "1.35rem", lineHeight: 1.5, color: "var(--mh-text)" }}>
              {outcome.title}
            </p>
            {outcome.why_it_matters && (
              <p className="mh-sans mt-2" style={{ fontSize: "1rem", lineHeight: 1.6, color: "var(--mh-muted)" }}>
                {outcome.why_it_matters}
              </p>
            )}
          </div>
        ) : (
          <p className="mh-sans" data-testid="northstar-no-outcome" style={{ fontSize: "1rem", color: "var(--mh-muted)" }}>
            A primary outcome for this strategy has not yet been chosen.
          </p>
        )}
      </div>
    </section>
  );
}

function StrategicDriver({ driver }) {
  if (!driver) {
    return (
      <section className="mh-fade" data-testid="driver-empty">
        <SectionLabel testid="driver-label">Primary Strategic Driver</SectionLabel>
        <p className="mh-sans" style={{ fontSize: "1rem", color: "var(--mh-muted)", maxWidth: "44ch" }}>
          No project is currently carrying the most weight toward your strategy.
        </p>
      </section>
    );
  }
  return (
    <section className="mh-fade" data-testid="driver">
      <SectionLabel testid="driver-label">Primary Strategic Driver</SectionLabel>
      <h3 className="mh-serif" data-testid="driver-title" style={{ fontSize: "1.7rem", lineHeight: 1.2, color: "var(--mh-text)" }}>
        {driver.title}
      </h3>
      {driver.purpose && (
        <p className="mh-serif mt-3" data-testid="driver-purpose" style={{ fontSize: "1.1rem", lineHeight: 1.6, color: "var(--mh-action)", maxWidth: "52ch" }}>
          {driver.purpose}
        </p>
      )}
      <div className="mt-5 space-y-3" style={{ maxWidth: "52ch" }}>
        {driver.next_action && (
          <div data-testid="driver-next-action">
            <span className="mh-label">Next action</span>
            <p className="mh-sans mt-1" style={{ color: "var(--mh-text)" }}>{driver.next_action}</p>
          </div>
        )}
        {driver.blocker && (
          <div data-testid="driver-blocker">
            <span className="mh-label" style={{ color: "var(--mh-accent)" }}>Blocked by</span>
            <p className="mh-sans mt-1" style={{ color: "var(--mh-action)" }}>{driver.blocker}</p>
          </div>
        )}
      </div>
    </section>
  );
}

function TodayItem({ item, onDone, busy }) {
  const [noting, setNoting] = useState(false);
  const [note, setNote] = useState("");
  const done = item.status === "done";

  return (
    <li
      data-testid={`today-item-${item.id}`}
      className="py-7 mh-fade"
      style={{ borderTop: "1px solid var(--mh-divider)" }}
    >
      <div className="flex items-start justify-between gap-6">
        <div style={{ opacity: done ? 0.5 : 1 }}>
          <span className="mh-label" data-testid={`today-item-type-${item.id}`}>
            {TYPE_LABELS[item.item_type] || item.item_type}
          </span>
          <p
            className="mh-serif mt-2"
            data-testid={`today-item-title-${item.id}`}
            style={{
              fontSize: "1.5rem",
              lineHeight: 1.25,
              color: "inherit",
              textDecoration: done ? "line-through" : "none",
            }}
          >
            {item.title}
          </p>
          {item.why_here && (
            <p className="mh-sans mt-2" style={{ fontSize: "0.95rem", color: "color-mix(in srgb, currentColor 58%, transparent)", maxWidth: "50ch" }}>
              {item.why_here}
            </p>
          )}
          {done && item.completion_note && (
            <p className="mh-sans mt-3" data-testid={`today-item-note-${item.id}`} style={{ fontSize: "0.9rem", color: "color-mix(in srgb, currentColor 78%, transparent)" }}>
              — {item.completion_note}
            </p>
          )}
        </div>

        {!done && !noting && (
          <button
            className="mh-btn mh-btn-ghost shrink-0"
            data-testid={`complete-btn-${item.id}`}
            onClick={() => setNoting(true)}
          >
            Complete
          </button>
        )}
        {done && (
          <span className="mh-label shrink-0" data-testid={`done-marker-${item.id}`} style={{ color: "var(--mh-accent)" }}>
            Done
          </span>
        )}
      </div>

      {noting && !done && (
        <div className="mt-4" data-testid={`complete-panel-${item.id}`}>
          <label className="mh-label" htmlFor={`note-${item.id}`}>A note for the record (optional)</label>
          <input
            id={`note-${item.id}`}
            className="mh-input mt-2"
            data-testid={`note-input-${item.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What made this matter?"
          />
          <div className="mt-3 flex gap-3">
            <button
              className="mh-btn mh-btn-primary"
              data-testid={`confirm-done-${item.id}`}
              disabled={busy}
              onClick={() => onDone(item.id, note.trim())}
            >
              Mark done
            </button>
            <button className="mh-btn mh-btn-ghost" data-testid={`cancel-done-${item.id}`} onClick={() => setNoting(false)}>
              Not yet
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function AddTodayForm({ projects, onCreate, busy }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [itemType, setItemType] = useState("strategic_priority");
  const [projectId, setProjectId] = useState("");
  const [why, setWhy] = useState("");

  const reset = () => { setTitle(""); setItemType("strategic_priority"); setProjectId(""); setWhy(""); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    const body = { title: title.trim(), item_type: itemType };
    if (why.trim()) body.why_here = why.trim();
    if (projectId) body.project_id = projectId;
    const ok = await onCreate(body);
    if (ok) reset();
  };

  if (!open) {
    return (
      <button className="mh-btn mh-btn-ghost mt-8" data-testid="open-add-form" onClick={() => setOpen(true)}>
        Choose an item for today
      </button>
    );
  }

  return (
    <form className="mt-8 mh-fade" data-testid="add-today-form" onSubmit={submit}>
      <div className="space-y-6" style={{ maxWidth: "48ch" }}>
        <div>
          <label className="mh-label" htmlFor="ti-title">Title</label>
          <input id="ti-title" className="mh-input mt-2" data-testid="input-title" value={title}
            onChange={(e) => setTitle(e.target.value)} placeholder="The one thing" required />
        </div>
        <div>
          <label className="mh-label" htmlFor="ti-type">Type</label>
          <select id="ti-type" className="mh-input mt-2" data-testid="select-type" value={itemType}
            onChange={(e) => setItemType(e.target.value)}>
            <option value="strategic_priority">Strategic Priority</option>
            <option value="real_life_necessity">Real-Life Necessity</option>
            <option value="blocker">Blocker</option>
          </select>
        </div>
        <div>
          <label className="mh-label" htmlFor="ti-project">Linked project (optional)</label>
          <select id="ti-project" className="mh-input mt-2" data-testid="select-project" value={projectId}
            onChange={(e) => setProjectId(e.target.value)}>
            <option value="">None</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mh-label" htmlFor="ti-why">Why it is here (optional)</label>
          <input id="ti-why" className="mh-input mt-2" data-testid="input-why" value={why}
            onChange={(e) => setWhy(e.target.value)} placeholder="Why this deserves today's attention" />
        </div>
        <div className="flex gap-3">
          <button type="submit" className="mh-btn mh-btn-primary" data-testid="submit-today-item" disabled={busy || !title.trim()}>
            Add to today
          </button>
          <button type="button" className="mh-btn mh-btn-ghost" data-testid="cancel-add" onClick={reset}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

function TodaySection({ items, projects, onCreate, onDone, busy }) {
  const full = items.length >= 3;
  return (
    <section className="mh-fade" data-testid="today-section">
      <div className="flex items-baseline justify-between">
        <SectionLabel testid="today-label">Today — Attention</SectionLabel>
        <span className="mh-label" data-testid="today-count">{items.length} of 3</span>
      </div>

      {items.length === 0 ? (
        <p className="mh-serif" data-testid="today-empty" style={{ fontSize: "1.4rem", lineHeight: 1.4, color: "color-mix(in srgb, currentColor 66%, transparent)", maxWidth: "34ch" }}>
          A blank canvas. Choose up to three things that genuinely deserve today.
        </p>
      ) : (
        <ul style={{ borderBottom: "1px solid var(--mh-divider)" }} data-testid="today-list">
          {items.map((item) => (
            <TodayItem key={item.id} item={item} onDone={onDone} busy={busy} />
          ))}
        </ul>
      )}

      {full ? (
        <p className="mh-sans mt-8" data-testid="today-full-note" style={{ color: "color-mix(in srgb, currentColor 80%, transparent)", fontSize: "0.95rem" }}>
          Today's three attention slots are filled. This is enough.
        </p>
      ) : (
        <AddTodayForm projects={projects} onCreate={onCreate} busy={busy} />
      )}
    </section>
  );
}

function UnresolvedReview({ items, busy, onRecommit, onDefer, onRemove }) {
  if (!items.length) return null;
  return (
    <section className="mh-fade" data-testid="unresolved-section">
      <SectionLabel testid="unresolved-label">Decisions Awaiting Clarification</SectionLabel>
      <p className="mh-sans" style={{ fontSize: "0.95rem", color: "var(--mh-muted)", maxWidth: "50ch", marginBottom: "0.5rem" }}>
        Selections from earlier days that were never resolved. Each simply needs a decision.
      </p>
      <ul>
        {items.map((item) => (
          <li key={item.id} data-testid={`unresolved-${item.id}`} className="py-6" style={{ borderTop: "1px solid var(--mh-divider)" }}>
            <span className="mh-label">{formatLongDate(item.date)} · {TYPE_LABELS[item.item_type] || item.item_type}</span>
            <p className="mh-serif mt-2" style={{ fontSize: "1.3rem", color: "var(--mh-text)" }}>{item.title}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button className="mh-btn mh-btn-primary" data-testid={`recommit-${item.id}`} disabled={busy} onClick={() => onRecommit(item)}>
                Recommit to today
              </button>
              <button className="mh-btn mh-btn-ghost" data-testid={`defer-${item.id}`} disabled={busy} onClick={() => onDefer(item)}>
                Defer
              </button>
              <button className="mh-btn mh-btn-ghost" data-testid={`remove-${item.id}`} disabled={busy} onClick={() => onRemove(item)}>
                No longer relevant
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function App({ onLogout }) {
  const dateISO = todayISO();
  const [view, setView] = useState("today");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editAlign, setEditAlign] = useState(false);
  const [alignment, setAlignment] = useState(null);
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [unresolved, setUnresolved] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [al, today, all, projs] = await Promise.all([
        api.currentAlignment(),
        api.todayForDate(dateISO),
        api.allTodayItems(),
        api.projects(),
      ]);
      setAlignment(al);
      setItems(today);
      setProjects(projs);
      setUnresolved(
        all.filter((it) => it.date < dateISO && it.status !== "done" && it.resolution == null),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dateISO]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    setFlash(null);
    setError(null);
    try {
      await fn();
      await load();
      if (successMsg) setFlash(successMsg);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = (body) => runAction(() => api.createTodayItem({ ...body, date: dateISO }));
  const handleDone = (id, note) => runAction(() => api.markDone(id, note), "Marked done.");
  const handleRecommit = (item) => runAction(() => api.recommit(item.id, dateISO, item.why_here), "Recommitted to today.");
  const handleDefer = (item) => runAction(() => api.defer(item.id), "Deferred.");
  const handleRemove = (item) => runAction(() => api.remove(item.id), "Set aside as no longer relevant.");

  return (
    <div className="min-h-screen" data-testid="today-page">
      <Header dateISO={dateISO} view={view} onNav={setView} onLogout={onLogout} />
      {view === "ventures" ? <Ventures /> : view === "capture" ? <Capture /> : (
      loading ? (
        <main className="mx-auto max-w-4xl px-6 sm:px-12 py-16">
          <p className="mh-sans" data-testid="loading-state" style={{ fontSize: "1rem", color: "var(--mh-muted)" }}>
            Gathering your chapter…
          </p>
        </main>
      ) : error && !alignment ? (
        <main className="mx-auto max-w-4xl px-6 sm:px-12 py-16" data-testid="fatal-error">
          <p className="mh-serif" style={{ fontSize: "1.4rem", color: "var(--mh-text)" }}>The archive is unreachable right now.</p>
          <p className="mh-sans mt-2" style={{ color: "var(--mh-muted)" }}>{error}</p>
          <button className="mh-btn mh-btn-ghost mt-6" data-testid="retry-btn" onClick={load}>Try again</button>
        </main>
      ) : (
        <main data-testid="today-main">
          {(flash || error) && (
            <div className="mx-auto max-w-4xl px-6 sm:px-12 pt-8">
              <div
                data-testid="flash-message"
                className="mh-fade"
                style={{
                  borderLeft: `2px solid ${error ? "var(--mh-accent)" : "var(--mh-action)"}`,
                  paddingLeft: "1rem",
                  color: error ? "var(--mh-action)" : "var(--mh-muted)",
                }}
              >
                <span className="mh-sans" style={{ fontSize: "0.95rem" }}>{error || flash}</span>
              </div>
            </div>
          )}

          <div className="mx-auto max-w-4xl px-6 sm:px-12 pt-10 sm:pt-14 pb-10 sm:pb-12">
            <div className="flex items-baseline justify-end mb-2">
              <button
                className="mh-label"
                data-testid="edit-alignment-toggle"
                onClick={() => setEditAlign((v) => !v)}
                style={{ background: "none", cursor: "pointer", color: "var(--mh-muted)" }}
              >
                {editAlign ? "Close" : "Edit chapter"}
              </button>
            </div>
            <NorthStar alignment={alignment} />
            {editAlign && (
              <AlignmentEditor alignment={alignment} onChanged={load} onClose={() => setEditAlign(false)} />
            )}
          </div>

          <div className="mh-band-taupe">
            <div className="mx-auto max-w-4xl px-6 sm:px-12 py-10 sm:py-12">
              <StrategicDriver driver={alignment?.primary_driver} />
            </div>
          </div>

          {unresolved.length > 0 && (
            <div className="mx-auto max-w-4xl px-6 sm:px-12 py-10 sm:py-12">
              <UnresolvedReview
                items={unresolved}
                busy={busy}
                onRecommit={handleRecommit}
                onDefer={handleDefer}
                onRemove={handleRemove}
              />
            </div>
          )}

          <div className="mh-band-dark">
            <div className="mx-auto max-w-4xl px-6 sm:px-12 py-12 sm:py-16">
              <TodaySection
                items={items}
                projects={projects}
                onCreate={handleCreate}
                onDone={handleDone}
                busy={busy}
              />
            </div>
          </div>
        </main>
      )
      )}
    </div>
  );
}
